import React, { useEffect } from 'react';
import { Sparkles, Heart, Leaf, ArrowRight, BookOpen, Share2, Facebook, Twitter, Mail, Copy, Star, Clock, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import NavBar from '@/components/NavBar';
import { BOOKING_URL } from '@/constants/urls';
import { WelcomeModal } from '@/components/WelcomeModal';
import BlogPostingSchema from '@/components/BlogPostingSchema';
import treatmentImage from '@/assets/treatment.webp';

const WinterSkinHealthBlog = () => {
  // Force scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);

    // Update document title and meta tags
    document.title = "Winter Skin Health: Cosmetic Acupuncture + Botanical Skincare | Women and the Way";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Discover the inside-out approach to glowing winter skin using Cosmetic Acupuncture and award-winning Australian botanicals. Book your treatment in Currimundi.");
    }

    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Winter Skin Health: How Cosmetic Acupuncture + Botanical Skincare Work Wonders");
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Discover the inside-out approach to glowing skin using Traditional Chinese Medicine and award-winning Australian botanicals.");
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', "https://womenandtheway.com.au/blog/winter-skin-health");
    }

    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', treatmentImage);
    }

    // Update Twitter Card
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', "Winter Skin Health: Cosmetic Acupuncture + Botanical Skincare");
    }

    // Cleanup function
    return () => {
      document.title = "Acupuncture Sunshine Coast & Caloundra | Women and The Way - Chinese Medicine";
    };
  }, []);

  const handleShare = (platform: string) => {
    const url = window.location.href;
    const title = "Winter Skin Health: Cosmetic Acupuncture + Botanical Skincare";
    const text = "Discover the inside-out approach to glowing skin using Traditional Chinese Medicine and award-winning Australian botanicals.";
    
    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'email':
        const subject = "Winter Skin Health: Cosmetic Acupuncture + Botanical Skincare";
        const body = `${text}\n\nRead more: ${url}`;
        window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
        break;
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  return (
    <>
      <BlogPostingSchema
        title="Winter Skin Health: How Cosmetic Acupuncture + Botanical Skincare Work Wonders"
        description="Discover the inside-out approach to glowing skin using Traditional Chinese Medicine and award-winning Australian botanicals."
        image={treatmentImage}
        url="https://womenandtheway.com.au/blog/winter-skin-health"
        publishedDate="2025-07-11"
        modifiedDate="2025-07-11"
                        keywords="winter skin health, cosmetic acupuncture, botanical skincare, skin treatment, Chinese medicine, Sunshine Coast, Currimundi"
        articleSection="Skin Health & Beauty"
      />
      <NavBar />
      <div className="min-h-screen bg-warm-beige pt-24 md:pt-32">
        {/* Hero Section with Image */}
        <section className="relative py-16 px-6 bg-gradient-to-r from-sage-50 via-white to-amber-50">
          <div className="container mx-auto max-w-6xl">
            <div className="flex flex-col md:flex-row items-center md:justify-between">
              <div className="w-full md:w-1/2 mb-10 md:mb-0">
                <div className="mb-8">
                  <span className="text-5xl md:text-6xl mb-4 block animate-float">❄️</span>
                  <h1 className="font-serif text-4xl md:text-5xl font-bold mb-6 leading-tight bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                    Winter Skin Health: Cosmetic Acupuncture + Botanical Skincare
                  </h1>
                  <p className="text-xl text-gray-600 font-light">
                    Discover the inside-out approach to glowing skin using Traditional Chinese Medicine and award-winning Australian botanicals.
                  </p>
                </div>
              </div>
              <div className="w-full md:w-5/12 relative">
                <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-amber-200/50">
                  <img 
                    src={treatmentImage} 
                    alt="Cosmetic acupuncture treatment for winter skin health at Women and the Way, Sunshine Coast – professional facial treatment with botanical skincare" 
                    className="w-full h-auto object-cover aspect-[4/3]"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-lg shadow-lg border border-amber-200/50 w-3/4 md:w-1/2">
                  <p className="text-sage-800 font-serif font-medium">
                    "Skin that glows starts beneath the surface."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <main className="py-12 px-6">
          <div className="container mx-auto max-w-3xl">
            {/* Introduction */}
            <section className="mb-16">
              <h2 className="font-serif text-3xl font-bold mb-8 flex items-center">
                <span className="text-3xl mr-3">✨</span>
                <span className="bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Skin That Glows Starts Beneath the Surface</span>
              </h2>
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm mb-8 rounded-2xl">
                <CardContent className="p-8">
                  <p className="text-lg leading-relaxed text-gray-700 mb-6">
                    Winter can be tough on your skin. Dryness, dullness, and inflammation are common complaints - especially when the weather cools and the wind picks up. At <a href="/" className="text-sage-600 hover:text-sage-800 underline transition-colors">Women and the Way</a>, we believe that skin isn't just skin - it's a reflection of your inner state of health, hormones, and stress.
                  </p>
                  <p className="text-lg leading-relaxed text-gray-700 mb-6">
                    That's why we take an inside-out approach using <a href="/cosmetic-acupuncture" className="text-sage-600 hover:text-sage-800 underline transition-colors">Cosmetic Acupuncture</a>, Chinese Medicine, and plant-based skincare to help you reclaim your natural radiance - especially in the colder months.
                  </p>
                  <p className="text-lg leading-relaxed text-gray-700">
                    This winter, we're pairing our gentle but powerful facial treatments with Founders Formula, an award-winning local skincare brand powered by Australian botanicals. Together, these treatments restore vitality, promote collagen production, and soothe inflammation - without harsh chemicals or quick fixes.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* What Makes Our Winter Skin Rituals Different */}
            <section className="mb-16">
              <h2 className="font-serif text-3xl font-bold mb-8 flex items-center">
                <span className="text-3xl mr-3">🧬</span>
                <span className="bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">What Makes Our Winter Skin Rituals Different?</span>
              </h2>
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm mb-8 rounded-2xl">
                <CardContent className="p-8">
                  <p className="text-lg leading-relaxed text-gray-700">
                    Traditional facials work from the outside in. We do the opposite. Our <a href="/cosmetic-acupuncture" className="text-sage-600 hover:text-sage-800 underline transition-colors">Cosmetic Acupuncture</a> sessions target the root causes of your skin concerns: digestive sluggishness, hormonal imbalances, stress, and systemic inflammation. At the same time, we nourish and hydrate the skin barrier with premium botanical actives - leaving you glowing from the inside out.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* The 5-Step Winter Skin Ritual */}
            <section className="mb-16">
              <h2 className="font-serif text-3xl font-bold mb-8 flex items-center">
                <span className="text-3xl mr-3">🌿</span>
                <span className="bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">The 5-Step Winter Skin Ritual (Inspired by Founders Formula)</span>
              </h2>
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm mb-8 rounded-2xl">
                <CardContent className="p-8">
                  <p className="text-lg leading-relaxed text-gray-700 mb-6">
                    Here are five essential steps to nourish, protect, and rejuvenate your skin this winter - each of which is thoughtfully woven into our treatment protocols.
                  </p>
                  
                  <div className="space-y-8">
                    <div className="border-l-4 border-sage-green pl-6">
                      <h4 className="font-semibold text-xl mb-3 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">1. Oil-Based Cleansing for Barrier Support</h4>
                      <p className="text-gray-700 mb-3">
                        Harsh foaming cleansers can strip your skin, especially in winter. Instead, we use Macadamia Omega-7 Cleansing Oil by Founders Formula - a luxurious oil that removes impurities while hydrating and protecting your skin barrier with natural squalene and omega-rich oils.
                      </p>
                      <p className="text-sage-600 font-medium">
                        💡 Pro tip: Oil cleansing at night supports your skin's natural repair cycle and leaves you glowing by morning.
                      </p>
                    </div>

                    <div className="border-l-4 border-sage-green pl-6">
                      <h4 className="font-semibold text-xl mb-3 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">2. Exfoliate Weekly (But Gently)</h4>
                      <p className="text-gray-700 mb-3">
                        Winter skin tends to build up with dead cells and flakes. We recommend the Wild Pandanus Exfoliating Enzyme Grains - a soft, barrier-friendly exfoliant that leaves skin smooth without irritation.
                      </p>
                      <p className="text-gray-700">
                        In clinic, we prep the skin with this step before cosmetic acupuncture to ensure optimal absorption of nutrients and improve cell turnover.
                      </p>
                    </div>

                    <div className="border-l-4 border-sage-green pl-6">
                      <h4 className="font-semibold text-xl mb-3 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">3. Hydrate with a Serum Powerhouse</h4>
                      <p className="text-gray-700 mb-3">
                        Both our Express and Constitutional treatments include a serum application tailored to your skin. We often use Founders Formula's Australian Botanicals Pro-Aging Treatment Concentrate Serum, which is fortified with Vitamin B3, B5, and Hyaluronic Acid to deeply hydrate, soothe inflammation, and support elasticity.
                      </p>
                      <p className="text-sage-600 font-medium">
                        ✨ In winter, serums are your skin's best friend. Don't skip this step!
                      </p>
                    </div>

                    <div className="border-l-4 border-sage-green pl-6">
                      <h4 className="font-semibold text-xl mb-3 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">4. Moisturise for 72-Hour Hydration</h4>
                      <p className="text-gray-700 mb-3">
                        Yes - 72 hours! Founders Formula's Wild Rosella Hydrating Moisturiser is light, silky, and clinically proven to hydrate for three full days after application. This is the moisturiser we apply after cosmetic acupuncture to seal in nutrients and lock in that post-treatment glow.
                      </p>
                    </div>

                    <div className="border-l-4 border-sage-green pl-6">
                      <h4 className="font-semibold text-xl mb-3 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">5. Seal & Nourish with Botanical Facial Oils</h4>
                      <p className="text-gray-700 mb-3">
                        Facial oils rich in native Australian botanicals - like Kakadu Plum, Davidson Plum, and Emu Apple - are a key part of our finishing ritual. We use Founders Formula Nourishing Facial Oils, which have won global awards for their anti-aging and superfood benefits.
                      </p>
                      <p className="text-sage-600 font-medium">
                        💬 Client favourite? The scent, the glow, and the feeling of deep skin nourishment.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Introductory Offer */}
            <section className="mb-16">
              <h2 className="font-serif text-3xl font-bold mb-8 flex items-center">
                <span className="text-3xl mr-3">🌸</span>
                <span className="bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Introductory Offer – Available Until September</span>
              </h2>
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm mb-8 rounded-2xl">
                <CardContent className="p-8">
                  <p className="text-lg leading-relaxed text-gray-700 mb-6">
                    Ready to experience this full ritual?
                  </p>
                  <p className="text-lg leading-relaxed text-gray-700 mb-6">
                    We're now offering an <a href="/care-packages/skin-package" className="text-sage-600 hover:text-sage-800 underline transition-colors">Introductory Cosmetic Express Treatment</a> - a 60-minute ritual that blends everything above, plus:
                  </p>
                  
                  <div className="bg-sage-50 rounded-lg p-6 mb-6">
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start space-x-2">
                        <span className="text-amber-500">•</span>
                        <span>Facial & body acupuncture</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-amber-500">•</span>
                        <span>Lymphatic Gua Sha</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-amber-500">•</span>
                        <span>Australian Clay Mask + botanical mask</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-amber-500">•</span>
                        <span>LED light therapy (normally $40 – included free!)</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-amber-500">•</span>
                        <span>Pump & Glow finishing mask</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-amber-500">•</span>
                        <span>All powered by Founders Formula skincare</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-amber-50 rounded-lg p-6 mb-6">
                    <h4 className="font-semibold text-sage-800 mb-3">✨ Pricing:</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li><strong>Existing Clients:</strong> $185</li>
                      <li><strong>New Clients:</strong> $230 Initial | $190 Return</li>
                      <li><strong>10-Treatment Package:</strong> $1665 (Save $815)</li>
                    </ul>
                  </div>

                  <p className="text-amber-600 font-medium">
                    🗓 Offer ends September 30. Book early - spaces are limited!
                  </p>

                  <div className="text-center mt-6">
                    <Button
                      asChild
                      size="lg"
                      className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 text-lg font-medium"
                    >
                      <a
                        href={BOOKING_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Book Your Glow Session Now
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Want to Go Deeper */}
            <section className="mb-16">
              <h2 className="font-serif text-3xl font-bold mb-8 flex items-center">
                <span className="text-3xl mr-3">💆‍♀️</span>
                <span className="bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Want to Go Deeper?</span>
              </h2>
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm mb-8 rounded-2xl">
                <CardContent className="p-8">
                  <p className="text-lg leading-relaxed text-gray-700 mb-6">
                    Our <a href="/care-packages/skin-package" className="text-sage-600 hover:text-sage-800 underline transition-colors">Constitutional Cosmetic Acupuncture</a> sessions offer deeper healing for those dealing with hormonal acne, rosacea, or persistent dryness. These 90–120 minute sessions include hot stone massage, body cupping, extended acupuncture, and blood analysis to treat the root cause of skin concerns.
                  </p>
                  
                  <div className="bg-sage-50 rounded-lg p-6">
                    <h4 className="font-semibold text-sage-800 mb-3">📍 Investment:</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li><strong>Initial:</strong> $320</li>
                      <li><strong>Return:</strong> $255</li>
                      <li><strong>Add Blood Analysis:</strong> $50</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Winter Skin Wisdom from Chinese Medicine */}
            <section className="mb-16">
              <h2 className="font-serif text-3xl font-bold mb-8 flex items-center">
                <span className="text-3xl mr-3">❄️</span>
                <span className="bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Winter Skin Wisdom from Chinese Medicine</span>
              </h2>
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm mb-8 rounded-2xl">
                <CardContent className="p-8">
                  <p className="text-lg leading-relaxed text-gray-700">
                    In Chinese Medicine, winter is governed by the Kidneys, which store our deep reserves of energy (Jing). When Kidney Yang is low, we see signs of depletion in the skin - dryness, fatigue, and poor circulation.
                  </p>
                  <p className="text-lg leading-relaxed text-gray-700 mt-4">
                    By supporting the Kidney and Spleen systems, improving digestion, calming the nervous system, and moving blood to the face, we help your skin regain its vitality without harsh interventions.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* Ask Us Anything */}
            <section className="mb-16">
              <h2 className="font-serif text-3xl font-bold mb-8 flex items-center">
                <span className="text-3xl mr-3">💌</span>
                <span className="bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Ask Us Anything</span>
              </h2>
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm mb-8 rounded-2xl">
                <CardContent className="p-8">
                  <p className="text-lg leading-relaxed text-gray-700 mb-6">
                    Got a question about your skin, the treatments, or whether <a href="/cosmetic-acupuncture" className="text-sage-600 hover:text-sage-800 underline transition-colors">Cosmetic Acupuncture</a> is right for you? We'd love to hear from you.
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl">📞</span>
                      <span className="text-gray-700">Call us at <a href="tel:0415537784" className="text-sage-600 hover:text-sage-800 underline transition-colors">0415 537 784</a></span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl">📩</span>
                      <span className="text-gray-700">Or <a href="/#contact" className="text-sage-600 hover:text-sage-800 underline transition-colors">message us here</a></span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Final CTA */}
            <section className="mb-16">
              <Card className="bg-gradient-to-r from-sage-50 to-amber-50 border-2 border-amber-200/50 shadow-sm rounded-2xl">
                <CardContent className="p-8 text-center">
                  <h3 className="font-serif text-2xl font-bold mb-4 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                    Book Now & Let Your Skin Glow Through Winter
                  </h3>
                  <p className="text-lg leading-relaxed text-gray-700 mb-6">
                    Your skin is speaking - we're here to listen. Whether you're preparing for summer, healing from the inside, or just craving that dewy, rested glow... this is your invitation to start.
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button
                      asChild
                      size="lg"
                      className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 text-lg font-medium"
                    >
                      <a
                        href={BOOKING_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        ✨ Reserve Your Appointment
                      </a>
                    </Button>
                    <Button
                      asChild
                      variant="outline"
                      size="lg"
                      className="border-sage-600 text-sage-600 hover:bg-sage-50 px-8 py-4 text-lg font-medium"
                    >
                      <a href="/care-packages/skin-package">
                        Learn More About Treatments
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Share Section */}
            <section className="mb-16">
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm rounded-2xl">
                <CardContent className="p-8">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="font-serif text-xl font-bold text-sage-800">Share This Article</h3>
                    <Share2 className="h-5 w-5 text-sage-600" />
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={() => handleShare('facebook')}
                      variant="outline"
                      size="sm"
                      className="flex items-center space-x-2"
                    >
                      <Facebook className="h-4 w-4" />
                      <span>Facebook</span>
                    </Button>
                    <Button
                      onClick={() => handleShare('twitter')}
                      variant="outline"
                      size="sm"
                      className="flex items-center space-x-2"
                    >
                      <Twitter className="h-4 w-4" />
                      <span>Twitter</span>
                    </Button>
                    <Button
                      onClick={() => handleShare('email')}
                      variant="outline"
                      size="sm"
                      className="flex items-center space-x-2"
                    >
                      <Mail className="h-4 w-4" />
                      <span>Email</span>
                    </Button>
                    <Button
                      onClick={handleCopyLink}
                      variant="outline"
                      size="sm"
                      className="flex items-center space-x-2"
                    >
                      <Copy className="h-4 w-4" />
                      <span>Copy Link</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </section>
          </div>
        </main>
      </div>
    </>
  );
};

export default WinterSkinHealthBlog; 